/// <reference path="../windowbroadcastmessage.ts" />
/// <reference path="windowbroadcastservice.ts" />
/// <reference path="logservice.ts" />
/// <reference path="../istateoptions.d.ts" />
/// <reference path="../istateoptions.d.ts" />
/// <reference path="navigationhelperservice.ts" />
/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../../apimodel.d.ts" />
/// <reference path="../oauthtoken.ts" />
/// <reference path="apiservice.ts" />
/// <reference path="../ilocalstorage.d.ts" />
/// <reference path="globalsettingsservice.ts" />
/// <reference path="usersservice.ts" />
/// <reference path="navigationhelperservice.ts" />
/// <reference path="../ioautherror.d.ts" />

module App.Common.Services {
	'use strict';

	export class AuthenticationService {

		public events = {
			signout: 'signout'
		}

		public model: Common.OAuthToken;

		public static $inject = [
			'$localStorage',
			'$sessionStorage',
			'$q',
			'$state',
			'$stateParams',
			'$rootScope',
			'$location',
			'app.common.services.logService',
			'app.common.services.globalSettingsService',
			'app.common.services.apiService',
			'app.common.services.windowBroadcastService',
			'app.common.services.usersService',
			'app.common.services.userMessagesService',
			'app.common.services.errorHandlerService',
			'app.common.services.navigationHelperService',
			'app.common.services.dateHelperService',
			'$cookies'
		];

		constructor(
			private $localStorage: Common.ILocalStorage,
			private $sessionStorage: Common.ISessionStorage,
			private $q: angular.IQService,
			private $state: angular.ui.IStateService,
			private $stateParams: angular.ui.IStateParamsService,
			private $rootScope: angular.IRootScopeService,
			private $location: angular.ILocationService,
			private logger: Services.LogService,
			private globalSettings: Services.GlobalSettingsService,
			private api: Services.ApiService,
			private windowBroadcast: Services.WindowBroadcastService,
			private users: Services.UsersService,
			private userMessages: Services.UserMessagesService,
			private errorHandler: Services.ErrorHandlerService,
			private navigationHelper: Services.NavigationHelperService,
			private dateHelper: Services.DateHelperService,
			private $cookies: ng.cookies.ICookiesService) {

			//Respond to events fired from other windows
			$rootScope.$on(this.events.signout, () => { this.signOut(true, false); });
		}

		public lastAuthenticationError: string;

		public currentActiveDirectoryUsername() {
			return this.$localStorage.currentActiveDirectoryUsername;
		};

		public currentUsername(): string {
			return this.users.getCurrentUser().username;
		}

		public currentUserEmail(): string {
			return this.users.getCurrentUser().emailAddress;
		}

		public useWindowsAuthentication(): boolean {
			return this.globalSettings.useWindowsAuthentication;
		}

		public signIn(username: string, password: string): angular.IPromise<any> {

			this.logger.debug('Sign in');

			return this.signInFormsAuthenticationPost(username, password)
				.then((token: Common.OAuthToken): angular.IPromise<any> => {
					this.saveAuthToken(token);
					return this.users.populateCurrentUserLocalStorage().then(() => this.$q.resolve());
                }, (result: angular.IHttpPromiseCallbackArg<Common.IOAuthError>) => {
					this.deleteSignInDetails();
					var requestConfig = result ? result.config : {} as IApiRequestConfig;
					this.lastAuthenticationError = result && result.data && result.data.error_description
						? result.data.error_description
						: '';
					if (this.useWindowsAuthentication()
						&& result
						&& result.status === 400
						&& requestConfig.data
						&& !requestConfig.data.refresh_token) {
						//go to the unauthorized page
						this.$state.go('WindowsAuthenticationAccessDenied', null, {location: false,suppressUnsavedChangesWarning: true} as Common.IStateOptions);
					} else if (this.navigationHelper.currentScreenIsSignIn()) {
						this.userMessages.addMessage(this.lastAuthenticationError
							|| 'Sorry, something went wrong with the sign in process', UserMessageType.Error);
					} else {
						return this.errorHandler.handleHttpError(result);
					}
					return this.$q.reject();
				});
        }

		private signInFormsAuthenticationPost(username: string, password: string): angular.IPromise<any> {

			this.logger.debugVerbose('Get auth token from OAuth service');

			var postConfig: Common.IApiRequestConfig = {
				url: this.globalSettings.authenticationEndpoints.token,
				authenticated: false,
				data: `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&client_id=${encodeURIComponent(this.globalSettings.oAuthClientId)}`,
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded'
				},
				suppressHttpErrorDisplay: true
			};

			if (this.useWindowsAuthentication()) {
				postConfig.withCredentials = true;
			}

			return this.api.post(postConfig);
		}

		//Performs an initial kerberos handshake with the authentication service before requesting the OAuth token
		private signInWindowsAuthentication(): angular.IPromise<any> {

			this.logger.debug('Sign in for Windows authentication');

			var postConfig: Common.IApiRequestConfig = {
				url: this.globalSettings.authenticationEndpoints.negotiate,
				authenticated: false,
				data: `grant_type=password&username=undefined&password=undefined&client_id=${this.globalSettings.oAuthClientId}`,
				suppressHttpErrorDisplay: true
			};

			if (this.useWindowsAuthentication()) {
				postConfig.withCredentials = true;
			}

			return this.api.get(postConfig).then((username: string): angular.IPromise<any> => {
				this.deleteSignInDetails();
				this.$localStorage.currentActiveDirectoryUsername = username;
				return this.signIn(username, null);
			}, (result: angular.IHttpPromiseCallbackArg<any>) => {
				return this.errorHandler.handleHttpError(result);
			});
		}

		public checkWindowsAuthenticationSignIn(force: boolean = false): angular.IPromise<any> {

			this.logger.debugVerbose('Checking Windows authentication');
			if (this.useWindowsAuthentication() && (force || !this.isAuthenticatedUnexpired())) {
				return this.signInWindowsAuthentication();
			}
			return this.$q.resolve();
		}

		public ensureAuthentication(force: boolean = false): angular.IPromise<any> {

			this.logger.debug('Ensuring the user is authenticated');
			return !this.isAuthenticatedUnexpired() || force ? this.reauthenticate(force) : this.$q.resolve();
		}

		public reauthenticate(force: boolean = false): angular.IPromise<any> {

			this.logger.debug('Reauthenticating');

            if (!this.useWindowsAuthentication()) {
				return this.refreshAuthToken().then(() => {
					if (!this.isAuthenticatedUnexpired()) {
						this.navigationHelper.redirectToSignInScreen();
						return this.$q.reject();
					} else {
						return this.$q.resolve();
					}
                }, () => {
					this.navigationHelper.redirectToSignInScreen();
					return this.$q.reject();
				});
            } else {
				return this.checkWindowsAuthenticationSignIn(force);
			}
		}

		public passwordIsCorrectForCurrentUser(password: string): angular.IPromise<boolean> {

			if (!this.currentUserEmail()) {
				return this.$q.resolve(false);
			}

			return this.signInFormsAuthenticationPost(this.currentUserEmail(), password).then(() => {
				return this.$q.resolve(true);
			}, () => {
				return this.$q.resolve(false);
			});
		}

		public signOut = (returnToHome = true, broadcast = true) => {

			this.logger.debug('Signing out');

			if (this.useWindowsAuthentication()) {
				return;
			}
			this.deleteSignInDetails();
			if (this.navigationHelper.currentScreen && this.navigationHelper.currentScreen.isAuthenticationRequired()) {
				this.navigationHelper.redirectToSignInScreen(!returnToHome);
			} else {
				delete this.$sessionStorage.returnState;
				delete this.$sessionStorage.returnStateParams;
			}

			if (broadcast) {
				this.windowBroadcast.send(this.events.signout);
			}
		}

		public deleteSignInDetails() {

			//TODO: WW to CW - Rewrite in line with Help To Buy Changes
			this.logger.debug('Deleting sign in details');
			this.users.clearCurrentUserLocalStorage();
			//localStorage.removeItem('ims-authToken');
			delete this.$localStorage.authToken;
			delete this.$localStorage.currentActiveDirectoryUsername;
			delete this.$localStorage.sessionTimeout;

			this.$cookies.remove(this.globalSettings.cookieNames.authToken);
			this.$cookies.remove(this.globalSettings.cookieNames.windowsAuthTokenValid);

			var windowId = this.$sessionStorage.windowId; //Preserve window ID
			(this.$sessionStorage as any).$reset(); //Bug in typings file for ngstorage prevents correct call
			this.$sessionStorage.windowId = windowId;
		}

		public isAuthenticated(): boolean {

			return !!this.$localStorage.authToken && !!this.$localStorage.currentUser;
		}

		public isAuthenticatedUnexpired(): boolean {

			this.logger.debugVerbose('Checking if authentication has expired');

			//TODO: WW for WW - Fix
			//return true;

			//No auth token
			if (!this.$localStorage.authToken) {
				this.logger.debugVerbose('No authentication token found');
				return false;
			}

			//Using Windows authentication and the browser has been closed
			//NOTE: Closing and re-opening the browser forces reauthentication because there is no sign-out in this mode.
			//This session-cookie-based technique ensures Windows users can get updated permissions etc.
			if (this.useWindowsAuthentication() && !this.$cookies.get(this.globalSettings.cookieNames.windowsAuthTokenValid)) {
				this.logger.debugVerbose('Windows authentication cookie missing');
				return false;
			}

			//For authentication to be considered unexpired, the auth token's expiry date must not have elapsed,
			//the currentUser object in local storage must be valid and the authToken cookie must be valid.
			var result = new Date(this.$localStorage.authToken['.expires']) > new Date()
				&& !!this.$localStorage.currentUser
				&& !!this.$cookies.get(this.globalSettings.cookieNames.authToken);

			if (result) {
				this.logger.debugVerbose('Authentication is valid and unexpired');
			} else {
				this.logger.debugVerbose('Authentication data is invalid or expired');
			}

			return result;
		}

        public refreshAuthToken(): angular.IPromise<any> {

			if (this.isAuthenticatedUnexpired()) {
				return this.$q.resolve();
			}

			if (!this.$localStorage.authToken || !this.$localStorage.authToken.refresh_token) {
				return this.$q.reject();
			}

			var postConfig: Common.IApiRequestConfig = {
				url: this.globalSettings.authenticationEndpoints.token,
				authenticated: false,
				data: `grant_type=refresh_token&refresh_token=${encodeURIComponent(this.$localStorage.authToken.refresh_token)}&client_id=${encodeURIComponent(this.globalSettings.oAuthClientId)}`,
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded'
				}
			};

			if (this.useWindowsAuthentication()) {
				postConfig.withCredentials = true;
			}

			return this.api.post(postConfig).then((token: Common.OAuthToken): angular.IPromise<any> => {
				this.saveAuthToken(token);
				return this.$q.resolve(true);
			});
        }

		private saveAuthToken(token: Common.OAuthToken) {

			this.logger.debugVerbose('Saving auth token');

			this.$localStorage.authToken = token;

			if (this.useWindowsAuthentication()) {
				this.$cookies.put(this.globalSettings.cookieNames.windowsAuthTokenValid, 'true');
			}

			this.$cookies.remove(this.globalSettings.cookieNames.authToken);
			this.$cookies.putObject(this.globalSettings.cookieNames.authToken, this.$localStorage.authToken, {
				expires: new Date(_.now() + (parseInt(token.expires_in, 10) * 1000))
			});
		}
	}

	angular
		.module('app.common.services')
		.service('app.common.services.authenticationService', AuthenticationService);
}